import type { IChargeRepository, PaginatedResponse } from '@yawi/domain';
import { paginateArray } from '@yawi/domain';
import {
  ChargeType,
  ChargeStatus,
  type Charge,
  type ChargeSummary,
  type MemberChargeSummary,
  type CreateChargeDTO,
  type UpdateChargeDTO,
  type GenerateBatchChargesDTO,
  type ChargeQueryParams,
} from '@yawi/types';

// Mock data - cargos de prueba
const mockCharges: Charge[] = [
  // Pendiente - Diciembre 2025 (próximo a vencer)
  {
    id: 'chg-001',
    memberId: 'user-001',
    unitId: 'mock-unit-001',
    unitName: 'Torre A · Depto 301',
    type: ChargeType.MAINTENANCE,
    concept: 'Cuota de Mantenimiento',
    amount: 2500,
    balance: 2500,
    period: '2025-12',
    dueDate: '2025-12-15T00:00:00Z',
    status: ChargeStatus.PENDING,
    reference: null,
    notes: null,
    createdBy: null,
    createdAt: '2025-12-01T00:00:00Z',
    updatedAt: '2025-12-01T00:00:00Z',
    isOverdue: false,
    daysOverdue: 0,
  },
  // Pendiente y Vencido - Noviembre 2025
  {
    id: 'chg-002',
    memberId: 'user-001',
    unitId: 'mock-unit-001',
    unitName: 'Torre A · Depto 301',
    type: ChargeType.MAINTENANCE,
    concept: 'Cuota de Mantenimiento',
    amount: 2500,
    balance: 2500,
    period: '2025-11',
    dueDate: '2025-11-15T00:00:00Z',
    status: ChargeStatus.PENDING,
    reference: null,
    notes: null,
    createdBy: null,
    createdAt: '2025-11-01T00:00:00Z',
    updatedAt: '2025-11-01T00:00:00Z',
    isOverdue: true,
    daysOverdue: 18,
  },
  // Pago Parcial - Derrama
  {
    id: 'chg-003',
    memberId: 'user-001',
    type: ChargeType.EXTRA,
    concept: 'Derrama - Reparación Elevador',
    amount: 5000,
    balance: 3000, // Pagó $2,000
    period: null,
    dueDate: '2025-11-30T00:00:00Z',
    status: ChargeStatus.PARTIAL,
    reference: 'DER-2025-001',
    notes: 'Reparación urgente del elevador principal',
    createdBy: 'admin-001',
    createdAt: '2025-11-10T00:00:00Z',
    updatedAt: '2025-11-20T00:00:00Z',
    isOverdue: true,
    daysOverdue: 3,
  },
  // Multa pendiente
  {
    id: 'chg-004',
    memberId: 'user-001',
    type: ChargeType.FINE,
    concept: 'Multa - Ruido excesivo',
    amount: 500,
    balance: 500,
    period: null,
    dueDate: '2025-12-01T00:00:00Z',
    status: ChargeStatus.PENDING,
    reference: 'MUL-2025-015',
    notes: 'Reporte del 28 de noviembre',
    createdBy: 'admin-001',
    createdAt: '2025-11-28T00:00:00Z',
    updatedAt: '2025-11-28T00:00:00Z',
    isOverdue: true,
    daysOverdue: 2,
  },
  // Pagado - Octubre 2025
  {
    id: 'chg-005',
    memberId: 'user-001',
    type: ChargeType.MAINTENANCE,
    concept: 'Cuota de Mantenimiento',
    amount: 2500,
    balance: 0,
    period: '2025-10',
    dueDate: '2025-10-15T00:00:00Z',
    status: ChargeStatus.PAID,
    reference: null,
    notes: null,
    createdBy: null,
    createdAt: '2025-10-01T00:00:00Z',
    updatedAt: '2025-10-10T00:00:00Z',
    isOverdue: false,
    daysOverdue: 0,
  },
  // Pagado - Septiembre 2025
  {
    id: 'chg-006',
    memberId: 'user-001',
    type: ChargeType.MAINTENANCE,
    concept: 'Cuota de Mantenimiento',
    amount: 2500,
    balance: 0,
    period: '2025-09',
    dueDate: '2025-09-15T00:00:00Z',
    status: ChargeStatus.PAID,
    reference: null,
    notes: null,
    createdBy: null,
    createdAt: '2025-09-01T00:00:00Z',
    updatedAt: '2025-09-12T00:00:00Z',
    isOverdue: false,
    daysOverdue: 0,
  },
  // Pagado - Agosto 2025
  {
    id: 'chg-007',
    memberId: 'user-001',
    type: ChargeType.MAINTENANCE,
    concept: 'Cuota de Mantenimiento',
    amount: 2500,
    balance: 0,
    period: '2025-08',
    dueDate: '2025-08-15T00:00:00Z',
    status: ChargeStatus.PAID,
    reference: null,
    notes: null,
    createdBy: null,
    createdAt: '2025-08-01T00:00:00Z',
    updatedAt: '2025-08-14T00:00:00Z',
    isOverdue: false,
    daysOverdue: 0,
  },
];

export class MockChargeRepository implements IChargeRepository {
  // === Admin Queries ===

  async getAllCharges(params?: ChargeQueryParams): Promise<PaginatedResponse<Charge>> {
    let filtered = [...mockCharges];

    if (params?.status) {
      filtered = filtered.filter(c => c.status === params.status);
    }
    if (params?.type) {
      filtered = filtered.filter(c => c.type === params.type);
    }

    return paginateArray(filtered, params?.page || 1, params?.limit || 10);
  }

  async getSummary(): Promise<ChargeSummary> {
    const pending = mockCharges.filter(c => c.status === ChargeStatus.PENDING);
    const partial = mockCharges.filter(c => c.status === ChargeStatus.PARTIAL);
    const paid = mockCharges.filter(c => c.status === ChargeStatus.PAID);

    return {
      totalPending: pending.length,
      totalPartial: partial.length,
      totalPaid: paid.length,
      amountPending: pending.reduce((sum, c) => sum + c.balance, 0) + partial.reduce((sum, c) => sum + c.balance, 0),
      amountPaid: paid.reduce((sum, c) => sum + c.amount, 0),
    };
  }

  async getOverdueCharges(): Promise<Charge[]> {
    return mockCharges.filter(c => c.isOverdue && c.status !== ChargeStatus.PAID);
  }

  async getChargeById(id: string): Promise<Charge | null> {
    return mockCharges.find(c => c.id === id) || null;
  }

  async getMemberCharges(memberId: string, params?: ChargeQueryParams): Promise<PaginatedResponse<Charge>> {
    const memberCharges = mockCharges.filter(c => c.memberId === memberId);
    return paginateArray(memberCharges, params?.page || 1, params?.limit || 10);
  }

  async getMemberSummary(_memberId: string): Promise<MemberChargeSummary> {
    const pending = mockCharges.filter(c => c.status === ChargeStatus.PENDING || c.status === ChargeStatus.PARTIAL);
    const overdue = mockCharges.filter(c => c.isOverdue && c.status !== ChargeStatus.PAID);

    return {
      memberId: 'user-001',
      memberName: 'Usuario Mock',
      totalOwed: pending.reduce((sum, c) => sum + c.balance, 0),
      pendingAmount: pending.reduce((sum, c) => sum + c.balance, 0),
      overdueAmount: overdue.reduce((sum, c) => sum + c.balance, 0),
      pendingCount: pending.length,
      overdueCount: overdue.length,
      nextDueDate: '2025-12-15T00:00:00Z',
    };
  }

  // === Resident Queries ===

  async getMyCharges(): Promise<Charge[]> {
    // Return only pending/partial charges (not paid)
    return mockCharges.filter(c => c.status === ChargeStatus.PENDING || c.status === ChargeStatus.PARTIAL);
  }

  async getMyChargesAll(params?: ChargeQueryParams): Promise<PaginatedResponse<Charge>> {
    return paginateArray(mockCharges, params?.page || 1, params?.limit || 10);
  }

  async getMySummary(): Promise<MemberChargeSummary> {
    return this.getMemberSummary('user-001');
  }

  // === Commands ===

  async createCharge(_data: CreateChargeDTO): Promise<Charge> {
    throw new Error('Mock: createCharge not implemented');
  }

  async generateBatchCharges(_data: GenerateBatchChargesDTO): Promise<Charge[]> {
    return [];
  }

  async updateCharge(_id: string, _data: UpdateChargeDTO): Promise<Charge> {
    throw new Error('Mock: updateCharge not implemented');
  }

  async cancelCharge(_id: string): Promise<Charge> {
    throw new Error('Mock: cancelCharge not implemented');
  }

  async deleteCharge(_id: string): Promise<void> {
    // No-op for mock
  }
}
